/**
 * product-picker-lib.ts — Hàm thuần cho bộ chọn "Sản phẩm" ở khung soạn tin.
 * Tách riêng để test: nhãn giá, gom biến thể theo nhóm, tab nguồn, điền mẫu link, lựa chọn link nhớ lại.
 */
import type { Product } from '@/hooks/use-products'
import { i18n, currentLocale } from '@/lib/i18n'

// ── Giá & tồn kho ────────────────────────────────────────────────────

const vnd = (n: number) => `${new Intl.NumberFormat(currentLocale()).format(n)}₫`

/** Giá gọn cho dòng sản phẩm: "120.000₫", "100.000₫ – 200.000₫", "Liên hệ", "Miễn phí". */
export function formatPickerPrice(p: Pick<Product, 'priceType' | 'price' | 'priceMax'>): string {
  switch (p.priceType) {
    case 'free':
      return i18n.t('convCommerce:picker.price.free')
    case 'contact':
      return i18n.t('convCommerce:picker.price.contact')
    case 'description':
      return i18n.t('convCommerce:picker.price.seeDescription')
    case 'range':
      if (p.price != null && p.priceMax != null && p.priceMax !== p.price) return `${vnd(p.price)} – ${vnd(p.priceMax)}`
      if (p.price != null) return p.priceMax == null ? i18n.t('convCommerce:picker.price.from', { price: vnd(p.price) }) : vnd(p.price)
      return i18n.t('convCommerce:picker.price.contact')
    case 'fixed':
    default:
      return p.price != null ? vnd(p.price) : i18n.t('convCommerce:picker.price.contact')
  }
}

/** Giá của cả nhóm biến thể: một giá nếu như nhau, khoảng min–max nếu khác. */
export function formatGroupPrice(variants: Pick<Product, 'priceType' | 'price' | 'priceMax'>[]): string {
  const fixed = variants.filter((v) => v.priceType === 'fixed' && v.price != null).map((v) => v.price as number)
  if (fixed.length === variants.length && fixed.length > 0) {
    const min = Math.min(...fixed)
    const max = Math.max(...fixed)
    return min === max ? vnd(min) : `${vnd(min)} – ${vnd(max)}`
  }
  return variants[0] ? formatPickerPrice(variants[0]) : ''
}

export type StockTone = 'ok' | 'out' | 'unknown'

/** "Còn 12" / "Hết hàng" / null (nguồn không theo dõi tồn). */
export function stockLabel(stock: number | null | undefined): { text: string; tone: StockTone } | null {
  if (stock == null) return null
  if (stock <= 0) return { text: i18n.t('convCommerce:picker.stock.out'), tone: 'out' }
  return { text: i18n.t('convCommerce:picker.stock.left', { n: new Intl.NumberFormat(currentLocale()).format(stock) }), tone: 'ok' }
}

// ── Gom biến thể theo nhóm (externalParentId) ───────────────────────

export interface ProductGroup {
  key: string
  /** Tên nhóm: phần chung trước " - " của tên biến thể (Evotech đặt "<tên> - <thuộc tính>"). */
  title: string
  variants: Product[]
}

/** Nhãn biến thể: phần sau " - " của tên khi có tiền tố chung, không thì cả tên. */
export function variantLabel(name: string, groupTitle: string): string {
  const prefix = `${groupTitle} - `
  return name.startsWith(prefix) ? name.slice(prefix.length) || name : name
}

function baseName(name: string): string {
  const i = name.lastIndexOf(' - ')
  return i > 0 ? name.slice(0, i) : name
}

/**
 * Một dòng cho mỗi sản phẩm: biến thể cùng externalSource + externalParentId gộp lại, giữ thứ tự
 * xuất hiện đầu tiên. Sản phẩm không có nhóm (tạo trong eCDP) đứng riêng một dòng.
 * Gom trên TOÀN BỘ trang đã tải nên nhóm bị cắt ngang giữa hai trang vẫn về chung một dòng.
 */
export function groupProducts(items: Product[]): ProductGroup[] {
  const map = new Map<string, ProductGroup>()
  for (const p of items) {
    const key = p.externalSource && p.externalParentId ? `${p.externalSource}:${p.externalParentId}` : `id:${p.id}`
    const g = map.get(key)
    if (g) {
      if (!g.variants.some((v) => v.id === p.id)) g.variants.push(p)
    } else map.set(key, { key, title: p.name, variants: [p] })
  }
  for (const g of map.values()) {
    if (g.variants.length < 2) continue
    const bases = new Set(g.variants.map((v) => baseName(v.name)))
    g.title = bases.size === 1 ? [...bases][0] : g.variants[0].name
  }
  return [...map.values()]
}

// ── Tab nguồn ────────────────────────────────────────────────────────

export const SOURCE_LABELS: Record<string, string> = {
  evotech: 'Evotech',
  get internal() { return i18n.t('convCommerce:picker.sources.internal') },
}

export interface SourceTab {
  key: string
  label: string
  count: number
}

/**
 * Tab nguồn: chỉ nguồn có sản phẩm. Nguồn đồng bộ ngoài trước (nhiều hàng trước), "Tạo trong eCDP" cuối.
 * Không có nguồn ngoài nào thì vẫn hiện "Tạo trong eCDP" nếu có hàng.
 */
export function buildSourceTabs(counts: { source: string; count: number }[]): SourceTab[] {
  const ext = counts
    .filter((c) => c.source !== 'internal' && c.count > 0)
    .sort((a, b) => b.count - a.count || a.source.localeCompare(b.source))
  const internal = counts.find((c) => c.source === 'internal' && c.count > 0)
  return [...ext, ...(internal ? [internal] : [])].map((c) => ({
    key: c.source,
    label: SOURCE_LABELS[c.source] ?? c.source.charAt(0).toUpperCase() + c.source.slice(1),
    count: c.count,
  }))
}

// ── Mẫu link (bản sao của backend evotech-product-link.ts — chỉ để XEM TRƯỚC) ──

export type LinkType = 'webapp' | 'miniapp'

export const LINK_PLACEHOLDERS = ['{itemsCode}', '{itemNo}', '{parentNo}', '{code}', '{id}'] as const

/** Điền mẫu như máy chủ; null nếu thiếu giá trị cho biến đang dùng. Link thật do máy chủ dựng khi gửi. */
export function fillLinkTemplate(
  tpl: string | null | undefined,
  values: { itemsCode?: string | null; itemNo?: string | null; parentNo?: string | null; code?: string | null },
): string | null {
  if (!tpl) return null
  const itemNo = values.itemNo?.trim() || ''
  const parentNo = values.parentNo?.trim() || itemNo
  const code = values.code?.trim() || ''
  const itemsCode = values.itemsCode?.trim() || ''
  if (tpl.includes('{itemsCode}') && !itemsCode) return null
  if (tpl.includes('{itemNo}') && !itemNo) return null
  if (tpl.includes('{parentNo}') && !parentNo) return null
  if ((tpl.includes('{code}') || tpl.includes('{id}')) && !code) return null
  return tpl
    .replaceAll('{itemsCode}', encodeURIComponent(itemsCode))
    .replaceAll('{itemNo}', encodeURIComponent(itemNo))
    .replaceAll('{parentNo}', encodeURIComponent(parentNo))
    .replaceAll('{code}', encodeURIComponent(code))
    .replaceAll('{id}', encodeURIComponent(code))
}

export function productLinkValues(p: Pick<Product, 'externalId' | 'externalParentId' | 'externalCode' | 'code'>) {
  return { itemsCode: p.externalCode, itemNo: p.externalId, parentNo: p.externalParentId, code: p.code }
}

/** Kiểm tra nhanh phía giao diện (máy chủ kiểm lại): rỗng hợp lệ; https + có biến + tên miền cố định. */
export function linkTemplateError(raw: string): string | null {
  const t = raw.trim()
  if (!t) return null
  if (!/^https:\/\//i.test(t)) return i18n.t('convCommerce:picker.linkError.https')
  const unknown = (t.match(/\{[^}]*\}/g) ?? []).filter((p) => !(LINK_PLACEHOLDERS as readonly string[]).includes(p))
  if (unknown.length) return i18n.t('convCommerce:picker.linkError.unknownVars', { vars: unknown.join(', ') })
  if (!LINK_PLACEHOLDERS.some((p) => t.includes(p))) return i18n.t('convCommerce:picker.linkError.needVar')
  if (t.slice(8).split(/[/?#]/)[0].includes('{')) return i18n.t('convCommerce:picker.linkError.domainVar')
  return null
}

// ── Lựa chọn link nhớ theo người dùng ────────────────────────────────

const LAST_LINK_KEY = (userId: string) => `ecdp:product-link-type:${userId}`

export function readLastLinkType(userId: string | undefined): LinkType | null {
  if (!userId) return null
  try {
    const v = localStorage.getItem(LAST_LINK_KEY(userId))
    return v === 'webapp' || v === 'miniapp' ? v : null
  } catch {
    return null
  }
}

export function saveLastLinkType(userId: string | undefined, t: LinkType) {
  if (!userId) return
  try {
    localStorage.setItem(LAST_LINK_KEY(userId), t)
  } catch {
    /* trình duyệt chặn bộ nhớ — bỏ qua */
  }
}

/** Lựa chọn tô sẵn: lần trước nếu còn dùng được, không thì loại đầu tiên đã cấu hình. */
export function defaultLinkType(available: Record<LinkType, boolean>, last: LinkType | null): LinkType | null {
  if (last && available[last]) return last
  if (available.webapp) return 'webapp'
  if (available.miniapp) return 'miniapp'
  return null
}
